// Builds Google-recommended Event structured data (schema.org/Event) from an event document.
// Includes the recommended fields GSC flags as "missing" on minimal Event items:
// description, image, offers, performer, organizer, eventStatus, eventAttendanceMode.

const BASE_URL = 'https://events.jigsawconferences.co.uk';
const DEFAULT_EVENT_IMAGE = `${BASE_URL}/og-image.jpg`;

function isPastEvent(event) {
  const end = event?.dates?.end;
  if (!end) return false;
  const endDate = new Date(end);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return endDate < today;
}

function eventUrl(event) {
  if (event?.officialUrl) return event.officialUrl;
  if (event?.citySlug && event?.venueSlug && event?.slug) {
    return `${BASE_URL}/events/${event.citySlug}/${event.venueSlug}/${event.slug}`;
  }
  return BASE_URL;
}

/**
 * Returns a rich schema.org/Event object for embedding in ItemList / CollectionPage schema.
 * Falls back gracefully for fields the event doc may not have (image, price).
 */
export function buildEventSchemaItem(event) {
  if (!event) return null;
  const url = eventUrl(event);
  return {
    '@type': 'Event',
    name: event.name,
    startDate: event.dates?.start,
    endDate: event.dates?.end,
    eventStatus: isPastEvent(event)
      ? 'https://schema.org/EventCancelled'
      : 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: event.venueName,
      address: {
        '@type': 'PostalAddress',
        addressLocality: event.city,
        addressCountry: 'GB',
      },
    },
    description:
      event.shortDescription ||
      event.metaDescription ||
      `${event.name}${event.city ? ` in ${event.city}` : ''}`,
    image: event.imageUrl || DEFAULT_EVENT_IMAGE,
    url,
    organizer: {
      '@type': 'Organization',
      name: event.organiserName || 'Jigsaw Conferences',
      url: event.organiserWebsite || BASE_URL,
    },
    performer: {
      '@type': 'Organization',
      name: event.organiserName || 'Various Speakers',
    },
    offers: {
      '@type': 'Offer',
      url,
      price: '0',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
      validFrom: event.createdAt || event.dates?.start,
    },
  };
}

/** Returns a ListItem wrapper around a rich Event item, for ItemList.itemListElement. */
export function buildEventListItem(event, position) {
  return {
    '@type': 'ListItem',
    position,
    item: buildEventSchemaItem(event),
  };
}
